import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ArrowRight, Loader2, Mail, CheckCircle2, FileText, Lock, Search, Sparkles } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import TabletMockup from "./TabletMockup";

// Years offered in the dropdown.
const YEAR_OPTIONS = (() => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let y = now + 1; y >= 1995; y--) arr.push(y);
  return arr;
})();

const CONDITION_OPTS = [
  { v: "poor", label: "Poor — heavy wear, mechanical issues" },
  { v: "fair", label: "Fair — noticeable wear, runs reliably" },
  { v: "good", label: "Good — typical for its age" },
  { v: "very_good", label: "Very good — well maintained" },
  { v: "excellent", label: "Excellent — like new" },
];

const TITLE_OPTS = [
  { v: "clean", label: "Clean title" },
  { v: "rebuilt", label: "Rebuilt / Reconstructed" },
  { v: "salvage", label: "Salvage" },
  { v: "flood", label: "Flood / Water damage" },
  { v: "lemon", label: "Lemon Law buyback" },
  { v: "manufacturer_buyback", label: "Manufacturer buyback" },
];

const ACCIDENT_OPTS = [
  { v: "none", label: "No accidents" },
  { v: "minor", label: "Minor — fender bender, no frame damage" },
  { v: "moderate", label: "Moderate — repairable, no airbag deploy" },
  { v: "major", label: "Major — frame damage / airbag deploy" },
];

function usd(n) {
  if (typeof n !== "number") return "—";
  return "$" + Math.round(n).toLocaleString("en-US");
}

export default function CarValueCalculator({ defaultState = null, stateName = null }) {
  const navigate = useNavigate();
  const [makes, setMakes] = useState([]);
  const [modelsList, setModelsList] = useState([]);
  const [trims, setTrims] = useState([]);

  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [trim, setTrim] = useState("");
  const [mileage, setMileage] = useState("");
  const [condition, setCondition] = useState("good");
  const [zip, setZip] = useState("");
  const [stateCode, setStateCode] = useState(defaultState || "");
  const [titleStatus, setTitleStatus] = useState("clean");
  const [accident, setAccident] = useState("none");

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [emailValue, setEmailValue] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // VIN autofill
  const [vin, setVin] = useState("");
  const [vinDecoding, setVinDecoding] = useState(false);
  // When a VIN decode wants to pre-select a model, store its slug here.
  // The [make] useEffect will pick it up after fetching the model list
  // (avoiding the race where useEffect resets `model` right after we set it).
  const pendingModelRef = useRef(null);

  // Load makes once. We wait for hydration before firing so the Select
  // components are fully mounted and avoid React error #418 (hydration
  // mismatch) caused by prerendered HTML disagreeing with client output.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.valuationMakes();
        if (!alive) return;
        setMakes(r.makes || []);
      } catch {
        // non-fatal; user can still type model later
      }
    })();
    trackEvent("calculator_start", { state: defaultState || null });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When make changes, load models.
  useEffect(() => {
    if (!make) {
      setModelsList([]);
      setModel("");
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const r = await api.valuationModels(make);
        if (!alive) return;
        const list = r.models || [];
        setModelsList(list);
        // If a VIN decode requested a specific model for this make, try
        // to match it now that we have the list — otherwise reset.
        const pending = pendingModelRef.current;
        pendingModelRef.current = null;
        if (pending) {
          const found =
            list.find((m) => m.slug === pending || m.display.toLowerCase() === pending) ||
            list.find((m) => pending.startsWith(m.display.toLowerCase()) || m.display.toLowerCase().startsWith(pending));
          if (found) {
            // Defer setModel by a tick so Radix's SelectItems for this
            // make's models register first; otherwise the trigger keeps
            // showing the placeholder instead of the resolved label.
            setTrim("");
            setTrims(found.trims || []);
            setTimeout(() => {
              if (!alive) return;
              setModel(found.slug);
            }, 30);
            return;
          }
        }
        setModel("");
        setTrim("");
        setTrims([]);
      } catch {
        if (alive) setModelsList([]);
      }
    })();
    return () => { alive = false; };
  }, [make]);

  // When model changes, derive trim options.
  useEffect(() => {
    if (!model) {
      setTrims([]);
      setTrim("");
      return;
    }
    const found = modelsList.find((m) => m.slug === model);
    setTrims(found?.trims || []);
    setTrim("");
  }, [model, modelsList]);

  const canSubmit = useMemo(
    () => !!year && !!make && !!model && !!mileage && !!condition && !submitting,
    [year, make, model, mileage, condition, submitting]
  );

  // VIN decode: fetches Year/Make/Model from NHTSA and tries to map
  // returned make/model to our slugs. Fields the user already filled are
  // preserved (we only fill what's empty + matches our catalog).
  const handleDecodeVin = async () => {
    const v = (vin || "").trim().toUpperCase();
    if (v.length < 11) {
      toast.error("Enter a VIN (at least 11 characters)");
      return;
    }
    setVinDecoding(true);
    try {
      const d = await api.decodeVin(v);
      if (d.error && !d.year && !d.make) {
        toast.error(d.error || "Could not decode this VIN");
        return;
      }
      const filled = [];
      // Year
      if (d.year && !year) {
        setYear(String(d.year));
        filled.push("year");
      }
      // Make — match NHTSA "Toyota" / "TOYOTA" to our slug "toyota"
      if (d.make) {
        const norm = String(d.make).trim().toLowerCase();
        const found = makes.find(
          (m) => m.slug === norm || m.display.toLowerCase() === norm,
        );
        if (found) {
          // Stash the model slug we want — the [make] useEffect will
          // resolve it once the model list arrives.
          if (d.model) {
            pendingModelRef.current = String(d.model).trim().toLowerCase();
          }
          setMake(found.slug);
          filled.push("make");
          if (d.model) filled.push("model");
        }
      }
      if (filled.length === 0) {
        toast.message("VIN decoded but model isn't in our catalog yet — please pick the closest match.");
      } else {
        toast.success(`Auto-filled: ${filled.join(", ")}`);
      }
      trackEvent("calculator_vin_decode", { filled: filled.join(",") || "none" });
    } catch (e) {
      toast.error("Could not decode this VIN — please fill manually");
    } finally {
      setVinDecoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      toast.error("Please fill the required fields");
      return;
    }
    setSubmitting(true);
    setResult(null);
    setEmailSent(false);
    try {
      const payload = {
        year: parseInt(year, 10),
        make,
        model,
        trim: trim || null,
        mileage: parseInt(String(mileage).replace(/[^0-9]/g, ""), 10),
        condition,
        zip: zip || null,
        state: stateCode || null,
        title_status: titleStatus,
        accident_history: accident,
        source_path: window.location.pathname,
        user_agent: navigator.userAgent,
      };
      const r = await api.valuationCalculate(payload);
      setResult(r);
      trackEvent("calculator_result_view", {
        state: stateCode || null,
        make,
        model,
        year,
        coverage: r.coverage,
      });
      // Smooth scroll to result
      setTimeout(() => {
        document.getElementById("calculator-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not calculate the estimate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReport = async () => {
    const email = emailValue.trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSendingEmail(true);
    try {
      const r = await api.valuationEmailReport(result.lead_id, email);
      if (r?.ok) {
        setEmailSent(true);
        toast.success("Report sent to " + email);
        trackEvent("calculator_email_submit", { email_domain: email.split("@")[1] });
        trackEvent("calculator_pdf_report_sent", {});
      } else {
        toast.error("Could not send report — try again later");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send report");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleClickToBuilder = async () => {
    if (result?.lead_id) {
      api.valuationTrackBuilderClick(result.lead_id).catch(() => {});
    }
    trackEvent("calculator_to_bill_of_sale_click", {
      state: stateCode || null,
      make,
      model,
      year,
      has_email: !!emailSent,
    });
    // Prefill the builder via sessionStorage so it works regardless of router state.
    try {
      sessionStorage.setItem(
        "csd_calc_prefill",
        JSON.stringify({
          vehicle: {
            year: parseInt(year, 10),
            make: result?.model_display?.split(" ")[0] || make,
            model: modelsList.find((m) => m.slug === model)?.display || model,
            trim: trim || "",
            mileage: parseInt(String(mileage).replace(/[^0-9]/g, ""), 10),
          },
          state: stateCode || null,
          source: "calculator",
          ts: Date.now(),
        })
      );
    } catch {
      // ignore storage errors
    }
    navigate("/builder/new?source=calculator");
  };

  // State-specific hero is rendered by <TabletMockup/> — no need for a
  // network-loaded image anymore (no AI-slop stock photos, faster LCP).

  const estimate = result?.estimate;
  const coverage = result?.coverage;

  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--csd-primary-light)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-8 sm:pt-16 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-3">
              <Calculator size={14} /> Free tool · No signup
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              {stateName ? `${stateName} Private Sale Car Value Calculator` : "Private Sale Car Value Calculator"}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[var(--csd-text-secondary)] leading-relaxed">
              Estimate what your car could sell for in a private sale based on mileage, condition, title status and location.
              See how a private sale compares with trading it in at a dealer.
            </p>
          </div>
          <div className="lg:col-span-2">
            <TabletMockup />
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-5xl mx-auto px-6 pb-10">
        <form onSubmit={handleSubmit} className="csd-card p-6 sm:p-8 space-y-5" data-testid="calculator-form">
          {/* VIN autofill */}
          <div className="rounded-xl border border-dashed border-[var(--csd-primary-light)] bg-[var(--csd-primary-light)]/15 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[var(--csd-primary)]" />
              <span className="text-sm font-semibold text-[var(--csd-text-primary)]">Got the VIN? Skip the typing.</span>
            </div>
            <p className="text-xs text-[var(--csd-text-secondary)] mb-3">
              Paste the 17-character Vehicle ID Number and we'll auto-fill year, make and model from the official NHTSA database.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="calc-vin"
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17))}
                placeholder="e.g. 1HGCM82633A123456"
                className="h-11 font-mono tracking-wider uppercase"
              />
              <Button
                type="button"
                onClick={handleDecodeVin}
                disabled={vinDecoding || !vin}
                data-testid="calc-vin-decode"
                variant="outline"
                className="h-11 rounded-xl whitespace-nowrap border-[var(--csd-primary-light)] text-[var(--csd-primary)] hover:bg-[var(--csd-primary-light)]/30"
              >
                {vinDecoding ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Search size={14} className="mr-2" />}
                Auto-fill from VIN
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Year" required>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger data-testid="calc-year"><SelectValue placeholder="Select year" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {YEAR_OPTIONS.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Make" required>
              <Select value={make} onValueChange={setMake}>
                <SelectTrigger data-testid="calc-make"><SelectValue placeholder="Select make" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {makes.map((m) => (<SelectItem key={m.slug} value={m.slug}>{m.display}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Model" required>
              <Select value={model} onValueChange={setModel} disabled={!modelsList.length}>
                <SelectTrigger data-testid="calc-model"><SelectValue placeholder={modelsList.length ? "Select model" : "Pick make first"} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {modelsList.map((m) => (<SelectItem key={m.slug} value={m.slug}>{m.display}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Trim (optional)">
              <Select value={trim} onValueChange={setTrim} disabled={!trims.length}>
                <SelectTrigger data-testid="calc-trim"><SelectValue placeholder={trims.length ? "Select trim" : "—"} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {trims.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Mileage" required>
              <Input
                data-testid="calc-mileage"
                inputMode="numeric"
                value={mileage}
                onChange={(e) => setMileage(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                placeholder="e.g. 70000"
              />
            </Field>
            <Field label="Condition" required>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger data-testid="calc-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTS.map((c) => (<SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <details className="rounded-xl border border-[var(--csd-border)] p-4">
            <summary className="cursor-pointer text-sm font-semibold text-[var(--csd-text-primary)]">More details (optional — improves accuracy)</summary>
            <div className="grid sm:grid-cols-4 gap-4 mt-4">
              <Field label="ZIP code">
                <Input data-testid="calc-zip" value={zip} maxLength={5} onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, ""))} placeholder="90210" />
              </Field>
              <Field label="State">
                <Input data-testid="calc-state" value={stateCode} maxLength={2} onChange={(e) => setStateCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))} placeholder="CA" />
              </Field>
              <Field label="Title status">
                <Select value={titleStatus} onValueChange={setTitleStatus}>
                  <SelectTrigger data-testid="calc-title"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTS.map((t) => (<SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Accident history">
                <Select value={accident} onValueChange={setAccident}>
                  <SelectTrigger data-testid="calc-accident"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCIDENT_OPTS.map((a) => (<SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </details>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="calc-submit"
              className="csd-btn-primary h-12 px-6 rounded-xl text-sm font-semibold flex-1 sm:flex-none"
            >
              {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" />Calculating…</> : <>Calculate My Private Sale Price <ArrowRight size={14} className="ml-2" /></>}
            </Button>
            <p className="text-xs text-[var(--csd-text-secondary)] self-center inline-flex items-center gap-1.5">
              <Lock size={11} /> No signup. We don't sell your data.
            </p>
          </div>
        </form>
      </section>

      {/* RESULT */}
      {result && (
        <section id="calculator-result" className="max-w-5xl mx-auto px-6 pb-10" data-testid="calculator-result">
          {coverage === "unknown" ? (
            <div className="csd-card p-6 bg-amber-50 border-amber-200">
              <h3 className="font-heading text-xl font-bold mb-2">We don't have enough data for this model yet</h3>
              <p className="text-sm text-[var(--csd-text-secondary)] mb-4">
                Try a similar model from the top US makes, or leave your email and we'll notify you when it's available.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  data-testid="notify-email"
                  placeholder="you@example.com"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                />
                <Button onClick={handleSendReport} disabled={sendingEmail} className="csd-btn-primary rounded-xl whitespace-nowrap">
                  {sendingEmail ? <Loader2 className="animate-spin" size={14} /> : "Notify me"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="csd-card p-6 bg-gradient-to-br from-[var(--csd-primary-light)]/40 to-white">
                  <div className="text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-1">Estimated private sale range</div>
                  <div className="font-heading text-3xl font-extrabold text-[var(--csd-text-primary)]" data-testid="result-private">
                    {usd(estimate.private_sale.low)} – {usd(estimate.private_sale.high)}
                  </div>
                </div>
                <div className="csd-card p-6">
                  <div className="text-xs uppercase tracking-widest text-[var(--csd-text-secondary)] font-semibold mb-1">Estimated trade-in range</div>
                  <div className="font-heading text-2xl font-bold text-[var(--csd-text-secondary)]" data-testid="result-tradein">
                    {usd(estimate.trade_in.low)} – {usd(estimate.trade_in.high)}
                  </div>
                </div>
                <div className="csd-card p-6 bg-amber-50 border-amber-200">
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-1">Potential difference selling privately</div>
                  <div className="font-heading text-2xl font-extrabold text-amber-700" data-testid="result-diff">
                    Up to {usd(estimate.potential_difference)}
                  </div>
                </div>
              </div>

              <div
                className="mt-6 rounded-2xl p-5 sm:p-7 text-white"
                style={{ backgroundColor: "var(--csd-primary)" }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-white">Ready to sell privately?</h3>
                    <p className="mt-1 text-sm text-white/85 leading-relaxed">
                      Document the sale with a bill of sale. Both parties get a signed PDF.
                    </p>
                  </div>
                  <Button
                    onClick={handleClickToBuilder}
                    data-testid="result-to-builder"
                    className="bg-white text-[var(--csd-primary)] hover:bg-white h-11 px-5 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 w-full sm:w-auto"
                  >
                    Create Your Bill of Sale <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              </div>

              {!emailSent ? (
                <div className="mt-5 csd-card p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    <Mail className="text-[var(--csd-primary)] shrink-0 mt-0.5" size={18} />
                    <div className="flex-1">
                      <h4 className="font-heading font-bold text-base">Get your full car value report by email</h4>
                      <p className="text-sm text-[var(--csd-text-secondary)] mt-1">
                        Includes a detailed breakdown, condition / mileage / title impact, tips to maximize your selling price, and a private sale checklist.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        <Input
                          type="email"
                          data-testid="report-email"
                          placeholder="you@example.com"
                          value={emailValue}
                          onChange={(e) => setEmailValue(e.target.value)}
                          className="h-11"
                        />
                        <Button
                          onClick={handleSendReport}
                          disabled={sendingEmail}
                          data-testid="report-send"
                          className="csd-btn-primary rounded-xl h-11 whitespace-nowrap"
                        >
                          {sendingEmail ? <Loader2 className="animate-spin" size={14} /> : <>Send report</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 csd-card p-5 bg-[var(--csd-primary-light)]/40 border-[var(--csd-primary-light)]">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="text-[var(--csd-primary)]" size={18} />
                    <span><strong>Report sent.</strong> Check your inbox at {emailValue} (and your spam folder if you don't see it).</span>
                  </div>
                </div>
              )}

              <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
                <h4 className="font-semibold text-amber-900 text-sm mb-2 flex items-center gap-2"><FileText size={14} /> About this estimate</h4>
                <p className="text-xs text-amber-900 leading-relaxed">{result.disclaimer}</p>
              </div>
            </>
          )}
        </section>
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[var(--csd-text-primary)]">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
